import {
  ILibraryContentItemType,
  ISortBy,
  IUserLibraryEntitiesParameters,
} from '@ifit/gateway-wolf-athenaeum-service';
import isEqual from 'lodash.isequal';
import pickBy from 'lodash.pickby';
import flatten from 'lodash.flatten';
import { parseBool } from '../../../helpers/parsers';
import { getSortByToLabel, getSortOptions } from '../../../helpers/sorting';
import getFilterData from '../components/get-filter-data';
import { ensureOnSearchPage } from './on-search-page';

interface ISearchFilters {
  categories?: string[];
  continents?: string[];
  difficultyScores?: string[];
  hasCompleted?: string[];
  includedEntities?: ILibraryContentItemType[];
  languages?: string[];
  locationTypes?: string[];
  requiredEquipment?: string[];
  targetAreas?: string[];
  timeRanges?: string[];
  trainerIds?: string[];
  query?: string;
  sortBy?: ISortBy;
}

export interface ISearchStateChanged {
  currentFilters: ISearchFilters;
  appliedFilters: ISearchFilters;
}

let searchState: SearchState;

export function setSearchState(state: SearchState): void {
  searchState = state;
}

export function getSearchState(): SearchState {
  return searchState;
}

export class SearchState {
  public current: ISearchFilters;
  private applied: ISearchFilters;
  private default: ISearchFilters;
  public previous: ISearchFilters;
  private readonly stateObservers: Array<
    (params: ISearchStateChanged, appliedChanged?: boolean) => void
  > = [];
  private readonly sortByOptions = {
    true: getSortOptions(true),
    false: getSortOptions(false),
  };
  private searchedAtLeastOnce = false;

  public static defaultFilters: ISearchFilters = {
    languages: ['English'],
  };
  private readonly filterDataLabels: {
    label: string;
    dataLabel: string;
  }[] = getFilterData();

  constructor() {
    const filters = this.baseFilterState();
    this.default = filters;
    this.current = filters;
    this.applied = filters;
    this.previous = null;
  }

  private baseFilterState(): ISearchFilters {
    return {
      ...SearchState.defaultFilters,
      sortBy: ISortBy.Trending,
    };
  }

  public get pristine(): boolean {
    return isEqual(this.current, this.applied);
  }

  public get dirty(): boolean {
    return !this.pristine;
  }

  public get executedASearch(): boolean {
    return this.searchedAtLeastOnce;
  }

  public get appliedCount(): number {
    if (!this.applied) {
      return 0;
    }
    return Object.keys(this.applied).reduce((sum, key) => {
      const value = this.applied[key];
      if (
        Array.isArray(value) &&
        value.length &&
        !isEqual(value, SearchState.defaultFilters[key])
      ) {
        return sum + 1;
      }
      return sum;
    }, 0);
  }

  public get usingFilters(): boolean {
    return this.appliedCount > 0 || Boolean(this.applied?.query);
  }

  public get appliedWithLength(): ISearchFilters {
    return pickBy(this.applied, 'length');
  }

  public get appliedForAPI(): Partial<IUserLibraryEntitiesParameters> {
    const {
      categories,
      difficultyScores,
      hasCompleted,
      timeRanges,
      ...otherFields
    } = this.appliedWithLength;
    const response: Partial<IUserLibraryEntitiesParameters> = otherFields;

    if (categories) {
      response.categories = categories.map(category => ({ name: category }));
    }
    if (difficultyScores) {
      // Maps beginner/intermediate/advanced to the original 7-point intensity scale.
      response.difficultyScores = difficultyScores.flatMap(difficultyScore => {
        if (difficultyScore === 'beginner') {
          return [1, 2, 3];
        } else if (difficultyScore === 'intermediate') {
          return [4, 5];
        } else {
          return [6, 7];
        }
      });
    }
    if (hasCompleted && hasCompleted.length) {
      response.hasCompleted = parseBool(hasCompleted[0]);
    }
    if (timeRanges) {
      response.timeRanges = timeRanges.map(timeRange => {
        const split = timeRange.split('-');
        return [parseInt(split[0]), parseInt(split[1])];
      });
    }

    return response;
  }

  public get sortOptions(): ReturnType<typeof getSortOptions> {
    return this.sortByOptions[String(Boolean(this.current?.query))];
  }

  public get sortIndex(): number {
    return Object.keys(getSortByToLabel(Boolean(this.current?.query))).indexOf(
      this.current.sortBy,
    );
  }

  public setViewContext(filters: ISearchFilters): void {
    const newState: ISearchFilters = {
      ...SearchState.defaultFilters,
      ...(filters || {}),
    };
    if (!newState.sortBy) {
      newState.sortBy = newState.query ? ISortBy.Relevance : ISortBy.Trending;
    }

    this.searchedAtLeastOnce = false;
    if (!isEqual(newState, this.current)) {
      this.previous = this.applied;
      this.current = this.applied = this.default = newState;
      this.stateUpdated(false);
    }
  }

  public changeType(value: string): void {
    if (value === 'all') {
      value = null;
    }
    let changed = false;
    if (value && this.current.includedEntities?.[0] !== value) {
      this.current = {
        ...this.current,
        includedEntities: [value as ILibraryContentItemType],
      };
      changed = true;
    } else if (!value && this.current.includedEntities.length) {
      this.current = {
        ...this.current,
        includedEntities: [],
      };
      changed = true;
    }
    if (changed) {
      this.applyCurrentFilters();
    }
  }

  public changeQuery(value: string): void {
    if (!value) {
      value = null;
    }
    if (value !== this.current.query) {
      const queryWasSet = Boolean(this.current.query);
      const queryNowSet = Boolean(value);
      let sortBy = this.current.sortBy;
      if (!queryNowSet && sortBy === ISortBy.Relevance) {
        sortBy = ISortBy.Trending;
      } else if (queryNowSet && !queryWasSet) {
        sortBy = ISortBy.Relevance;
      }

      this.current = {
        ...this.current,
        sortBy,
        query: value,
      };
      this.stateUpdated(false);
    }
  }

  public changeSort(value: string): void {
    if (value !== this.current.sortBy) {
      this.current = {
        ...this.current,
        sortBy: value as ISortBy,
      };
      this.applyCurrentFilters();
    }
  }

  private convertIntensityToString(intensity: string): string {
    if (!parseInt(intensity, 10)) {
      return intensity;
    }

    const numericIntensity = parseInt(intensity, 10);
    if (numericIntensity >= 1 && numericIntensity <= 3) {
      return 'beginner';
    } else if (numericIntensity >= 4 && numericIntensity <= 5) {
      return 'intermediate';
    } else {
      return 'advanced';
    }
  }

  public changeFromQueryString(queryString: Record<string, string[]>): void {
    const filters = this.filterDataLabels.reduce(
      (accumulator, { dataLabel, label }) => {
        const qsFilters = flatten(
          [queryString[dataLabel], queryString[label]].filter(Boolean),
        );
        return {
          ...accumulator,
          [dataLabel]: qsFilters.length
            ? qsFilters
            : accumulator[dataLabel] || [],
        };
      },
      { ...SearchState.defaultFilters },
    );

    const filterHasValue = (filter, value): boolean =>
      filters[filter]?.includes(value);
    const removeFilterValue = (filter, value): void => {
      filters[filter] = filters[filter]?.filter(
        filterValue => filterValue !== value,
      );
    };
    if (filterHasValue('categories', 'International')) {
      removeFilterValue('categories', 'International');
      filters.languages = [];
    }
    if (filters.difficultyScores) {
      const scores = [].concat(filters.difficultyScores);
      filters.difficultyScores = scores.map(score => {
        return this.convertIntensityToString(score);
      });
    }
    if (queryString.query?.[0]) {
      filters.query = queryString.query[0];
    }
    if (queryString.type?.[0] && queryString.type[0] !== 'all') {
      filters.includedEntities = [
        queryString.type?.[0] as ILibraryContentItemType,
      ];
    }
    if (queryString.sortBy?.[0]) {
      filters.sortBy = queryString.sortBy?.[0] as ISortBy;
    }

    if (!filters.sortBy) {
      filters.sortBy = filters.query ? ISortBy.Relevance : ISortBy.Trending;
    }
    this.current = filters;
  }

  private checkSameSelect(array: string[], value: string): string {
    if (array.length === 0) {
      return 'alloptions';
    } else {
      return value;
    }
  }

  public changeFilter(
    filter: string,
    value: string,
    shouldApply = false,
  ): void {
    const existing = this.current[filter];
    let newFilterValues = [...(existing || [])];

    if (value === 'alloptions') {
      // clear all existing values
      newFilterValues = [];
    } else if (newFilterValues.includes(value)) {
      // remove if already added
      newFilterValues.splice(newFilterValues.indexOf(value), 1);
      //set value to 'alloptions' when clicking last checked select
      value = this.checkSameSelect(newFilterValues, value);
    } else {
      // else, add
      newFilterValues.push(value);
    }

    // Completed filter is radio style (only 1 can be picked)
    if (['true', 'false'].includes(value)) {
      if (value !== 'alloptions' && existing?.includes(value)) {
        newFilterValues = [];
      } else {
        newFilterValues = [value];
      }
    }

    if (!isEqual(newFilterValues, existing)) {
      this.current = {
        ...this.current,
        [filter]: newFilterValues,
      };

      if (value === 'alloptions') {
        delete this.current[filter];
      }

      if (shouldApply) {
        this.applyCurrentFilters();
      } else {
        this.stateUpdated(false);
      }
    }
  }

  public applyDefaultFilters(): void {
    const baseFilters = this.baseFilterState();
    this.current = this.applied = baseFilters;
    this.previous = null;
    this.searchedAtLeastOnce = true;
    this.stateUpdated(true);
  }

  public applyCurrentFilters(): void {
    if (ensureOnSearchPage() && !isEqual(this.applied, this.current)) {
      this.applied = this.current;
      this.searchedAtLeastOnce = true;
      this.stateUpdated(true);
    }
  }

  public applyPreviousFilters(): void {
    if (!isEqual(this.current, this.previous)) {
      this.applied = this.current = this.previous;
      this.searchedAtLeastOnce = true;
      this.stateUpdated(true);
    }
  }

  public resetPreviousFilters(): void {
    this.previous = null;
    this.searchedAtLeastOnce = true;
    this.stateUpdated(true);
  }

  public resetSearchState(): void {
    this.previous = null;
    this.applied = this.current = this.default;
    this.searchedAtLeastOnce = false;
    this.stateUpdated(false);
  }

  public applyDefaultSearchState(): any {
    const filters = this.baseFilterState();
    this.current = filters;
    this.applied = filters;
    this.previous = null;
    this.stateUpdated(true);
  }

  public removeFilterByKey(key: string): void {
    if (this.current && this.current[key]) {
      delete this.current[key];
    }
  }

  private stateUpdated(appliedChanged: boolean): void {
    for (const stateObserver of this.stateObservers) {
      stateObserver(
        {
          currentFilters: this.current,
          appliedFilters: this.applied,
        },
        appliedChanged,
      );
    }
  }

  public onStateChanged(
    fn: (params: ISearchStateChanged, appliedChanged?: boolean) => void,
  ): void {
    this.stateObservers.push(fn);
    fn({
      currentFilters: this.current,
      appliedFilters: this.applied,
    });
  }

  public offStateChanged(
    fn: (params: ISearchStateChanged, appliedChanged?: boolean) => void,
  ): void {
    const i = this.stateObservers.indexOf(fn);
    if (i >= 0) {
      this.stateObservers.splice(i, 1);
    }
  }
}

searchState = new SearchState();
